/* eslint-disable react-hooks/exhaustive-deps */
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import { useMemo, useEffect, useContext } from 'react';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Loading from '@/components/Loading';
import { LoginMFA } from '@/components/MFAAuth';
import { URL_ALLOW } from '@/constants';
import AuthContext from '@/contexts/auth';
import SchoolContext from '@/contexts/school';
import UserContext from '@/contexts/user';
import useCurrentUser from '@/hooks/useCurrentUser';

// import useEmail from '@/hooks/useEmail'
import { useUserChanged } from '@/hooks/useUserChanged';
type UserChanged = {
  children: React.ReactNode;
};

const UserChanged = ({ children }: UserChanged) => {
  const router = useRouter();
  const { slug } = useContext(SchoolContext);

  const { loginError } = useContext(AuthContext);

  const { processing, user: fbUser, updateMailaddress } = useUserChanged();

  const { data, error, mutate: mutateCurrentUser } = useCurrentUser(fbUser?.uid);

  const user = useMemo(() => {
    return data?.user;
  }, [data?.user]);

  const schoolSlug = useMemo(() => {
    return (router.query.schoolSlug as string) || '';
  }, [router.query.schoolSlug]);

  const tenantId = useMemo(() => {
    return (fbUser?.tenantId as string) || '';
  }, [fbUser?.tenantId]);

  const loginschool = useMemo(() => {
    const tmp = tenantId.match(/.+-(.+)$/);
    return tmp?.[0].replace('-' + tmp[1], '');
  }, [tenantId]);

  useEffect(() => {
    if (slug && !processing && !fbUser && !URL_ALLOW.includes(router.pathname)) {
      router.push(`/${slug}/login`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.pathname, slug, processing, fbUser]);

  // メールアドレス変更の取り消し後、
  // firebaseのemailとhasuraのemailが異なっていたらfirebaseのほうで更新する
  useEffect(() => {
    if (user?.email && fbUser?.email) {
      if (user.email !== fbUser.email) {
        updateMailaddress({
          id: user.id as number,
          email: fbUser.email,
        });
      }
    }
  }, [fbUser?.email, updateMailaddress, user?.email, user?.id, user?.school_role]);

  // 認証処理中はローディング表示
  if (processing) {
    return <Loading />;
  }
  if (error) {
    return <div>Error</div>;
  }

  /**
   * Show MFA Authentication (Except super admin)
   * Case 1: First Login, not set MFA authentication
   * Case 2: Login after setting MFA authentication
   */
  if (loginError && loginError.code == 'auth/multi-factor-auth-required') {
    return <LoginMFA />;
  }

  if (data && !tenantId.includes(schoolSlug)) {
    return (
      <Dialog visible={true} title='他スクールにログイン中です'>
        <Dialog.Body text={<span>他スクールへアクセスする際はログアウトしてください</span>} />
        <Dialog.Footer>
          <Button
            type='button'
            size='lg'
            variant='destructive'
            handleClick={() => {
              router.replace(`/${loginschool}`);
            }}>
            ログイン中のスクールに戻る
          </Button>
        </Dialog.Footer>
      </Dialog>
    );
  }

  return (
    <UserContext.Provider value={{ ...user, mutateCurrentUser }}>
      {user && !user?.canAccess ? (
        <ErrorPage statusCode={403} title='このページは存在しないか、アクセス権限がありません。' />
      ) : (
        children
      )}
    </UserContext.Provider>
  );
};

export default UserChanged;
